import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { format, startOfMonth, subDays } from "date-fns";
import toast from "react-hot-toast";
import {
  HiOutlineDownload,
  HiOutlineCollection,
  HiOutlineEye,
  HiOutlineCheckCircle,
  HiOutlineAdjustments,
  HiOutlineDocumentText,
  HiOutlineTable,
  HiCheck,
} from "react-icons/hi";
import { Modal, Button } from "../../components/UI";
import { leadsAPI } from "../../services/api";
import { formatLabel } from "../../utils/formatLabel";

// Lead disposition / priority / common source values (see LeadsList).
const STATUS_VALUES = [
  "new", "open", "in_progress", "follow_up", "cold", "warm", "hot",
  "converted", "closed", "lost", "not_connected",
];
const PRIORITY_VALUES = ["urgent", "high", "medium", "low"];
const SOURCE_VALUES = [
  "Website", "Referral", "Cold Call", "LinkedIn", "Email Campaign",
  "Social Media", "Advertisement", "Walk-in", "IndiaMART", "JustDial",
  "Facebook", "Google Sheet", "import", "app", "web_form", "call",
];

// Internal source values written by the app / integrations → readable labels
const SOURCE_LABELS = { import: "Imported", app: "Mobile App", web_form: "Web Form", call: "Incoming Call" };

const fmtDate = (d) => (d ? format(new Date(d), "yyyy-MM-dd HH:mm") : "");
const personName = (u) => (u && typeof u === "object" ? `${u.firstName || ""} ${u.lastName || ""}`.trim() : "");

// Every exportable column; `on` = ticked by default.
const buildColumns = (stageLabel) => [
  { key: "name", group: "contact", label: "Name", on: true, get: (l) => l.name },
  { key: "phone", group: "contact", label: "Phone", on: true, get: (l) => l.phone },
  { key: "alternatePhone", group: "contact", label: "Alternate Phone", on: true, get: (l) => l.alternatePhone },
  { key: "email", group: "contact", label: "Email", on: true, get: (l) => l.email },
  { key: "companyName", group: "company", label: "Company", on: false, get: (l) => l.companyName },
  { key: "designation", group: "company", label: "Designation", on: false, get: (l) => l.designation },
  { key: "stage", group: "pipeline", label: "Stage", on: true, get: (l) => stageLabel(l.stage) },
  { key: "status", group: "pipeline", label: "Status", on: true, get: (l) => (l.status ? formatLabel(l.status) : "") },
  { key: "priority", group: "pipeline", label: "Priority", on: true, get: (l) => (l.priority ? formatLabel(l.priority) : "") },
  { key: "source", group: "pipeline", label: "Source", on: true, get: (l) => l.source },
  { key: "assignedTo", group: "pipeline", label: "Assigned To", on: true, get: (l) => personName(l.assignedTo) },
  { key: "leadScore", group: "pipeline", label: "Lead Score", on: false, get: (l) => l.leadScore ?? "" },
  { key: "tags", group: "pipeline", label: "Tags", on: false, get: (l) => (l.tags || []).join("; ") },
  { key: "products", group: "pipeline", label: "Products", on: false, get: (l) => (l.productIds || []).map((p) => p?.name).filter(Boolean).join("; ") },
  { key: "city", group: "company", label: "City", on: false, get: (l) => l.address?.city },
  { key: "state", group: "company", label: "State", on: false, get: (l) => l.address?.state },
  { key: "country", group: "company", label: "Country", on: false, get: (l) => l.address?.country },
  { key: "pincode", group: "company", label: "Pincode", on: false, get: (l) => l.address?.pincode },
  { key: "industry", group: "company", label: "Industry", on: false, get: (l) => l.industry },
  { key: "website", group: "company", label: "Website", on: false, get: (l) => l.website },
  { key: "gstNumber", group: "company", label: "GST Number", on: false, get: (l) => l.gstNumber },
  { key: "createdAt", group: "activity", label: "Created At", on: true, get: (l) => fmtDate(l.createdAt) },
  { key: "createdBy", group: "activity", label: "Created By", on: false, get: (l) => personName(l.createdBy) },
  { key: "lastContactedAt", group: "activity", label: "Last Contacted", on: false, get: (l) => fmtDate(l.lastContactedAt) },
  { key: "nextFollowUpAt", group: "activity", label: "Next Follow-up", on: false, get: (l) => fmtDate(l.nextFollowUpAt) },
  { key: "notes", group: "activity", label: "Notes", on: true, get: (l) => l.notes },
];

const COLUMN_GROUPS = [
  { key: "contact", label: "Contact" },
  { key: "pipeline", label: "Pipeline" },
  { key: "company", label: "Company & Address" },
  { key: "activity", label: "Activity" },
];

const COLUMNS_KEY = "leadExportColumns";
const loadSavedColumns = (columns) => {
  try {
    const saved = JSON.parse(localStorage.getItem(COLUMNS_KEY) || "null");
    if (Array.isArray(saved) && saved.length > 0) return saved.filter((k) => columns.some((c) => c.key === k));
  } catch {
    // ignore — fall back to defaults
  }
  return columns.filter((c) => c.on).map((c) => c.key);
};

const today = () => format(new Date(), "yyyy-MM-dd");
const DATE_PRESETS = [
  { key: "today", label: "Today", range: () => [today(), today()] },
  { key: "7d", label: "Last 7 days", range: () => [format(subDays(new Date(), 6), "yyyy-MM-dd"), today()] },
  { key: "30d", label: "Last 30 days", range: () => [format(subDays(new Date(), 29), "yyyy-MM-dd"), today()] },
  { key: "month", label: "This month", range: () => [format(startOfMonth(new Date()), "yyyy-MM-dd"), today()] },
];

// Local calendar day → ISO bounds, so "to" includes the whole day in the
// viewer's timezone.
const dayStart = (d) => (d ? new Date(`${d}T00:00:00`).toISOString() : undefined);
const dayEnd = (d) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : undefined);

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs sm:text-sm font-medium transition-all ${
      active
        ? "bg-primary-600 border-primary-600 text-white shadow-sm"
        : "bg-white border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-700 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300"
    }`}
  >
    {active && <HiCheck className="w-3.5 h-3.5 -ml-0.5" />}
    {children}
  </button>
);

// Filter group: label, "n selected" badge and a clear link
const Section = ({ title, count, onClear, children, className = "" }) => (
  <div className={className}>
    <div className="flex items-center gap-2 mb-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">{title}</span>
      {count > 0 && (
        <span className="px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold leading-none dark:bg-primary-500/20 dark:text-primary-300">
          {count}
        </span>
      )}
      {count > 0 && onClear && (
        <button type="button" onClick={onClear} className="ml-auto text-xs text-gray-400 hover:text-red-500">
          Clear
        </button>
      )}
    </div>
    {children}
  </div>
);

// Numbered step heading
const Step = ({ n, title, right }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold shrink-0">{n}</span>
    <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-slate-100">{title}</h3>
    {right && <div className="ml-auto">{right}</div>}
  </div>
);

// Card-style single choice with a drawn radio (native radios are restyled
// by global input CSS)
const ChoiceCard = ({ selected, disabled, onSelect, icon: Icon, title, description, badge }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    onClick={onSelect}
    className={`relative w-full text-left flex items-start gap-3 p-3.5 rounded-xl border-2 transition-all ${
      selected
        ? "border-primary-500 bg-primary-50/70 shadow-sm dark:bg-primary-500/10"
        : "border-gray-200 bg-white hover:border-primary-200 hover:bg-gray-50/60 dark:bg-slate-800/60 dark:border-slate-700"
    } ${disabled ? "opacity-50 cursor-not-allowed hover:border-gray-200 hover:bg-white" : "cursor-pointer"}`}
  >
    <span
      className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${
        selected ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-300"
      }`}
    >
      <Icon className="w-5 h-5" />
    </span>
    <span className="flex-1 min-w-0 pr-5">
      <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-slate-100">
        {title}
        {badge !== undefined && (
          <span className="px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[11px] font-semibold dark:bg-slate-700 dark:text-slate-300">
            {badge}
          </span>
        )}
      </span>
      <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5 leading-snug">{description}</span>
    </span>
    <span
      className={`absolute top-3.5 right-3.5 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
        selected ? "border-primary-600" : "border-gray-300 dark:border-slate-500"
      }`}
    >
      {selected && <span className="w-2 h-2 rounded-full bg-primary-600" />}
    </span>
  </button>
);

const toggleIn = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

/**
 * Export popup: choose which leads (all / current view / selected / custom
 * filters by stage, status, priority, source, assignee, created date, last
 * contacted, product, keyword), which columns, and CSV or Excel.
 */
const ExportLeadsModal = ({
  isOpen,
  onClose,
  initialScope = "all",
  currentFilters,
  selection,
  stageOptions,
  users,
  products,
}) => {
  const stageLabel = useMemo(() => {
    const map = Object.fromEntries((stageOptions || []).map((s) => [s.value, s.label]));
    return (key) => (key ? map[key] || formatLabel(key) : "");
  }, [stageOptions]);
  const columns = useMemo(() => buildColumns(stageLabel), [stageLabel]);

  const hasSelection = selection.mode === "count" || selection.ids.length > 0;
  const selectedCount = selection.mode === "count" ? selection.count : selection.ids.length;

  const [scope, setScope] = useState(initialScope);
  const [stages, setStages] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [sources, setSources] = useState([]);
  const [assignment, setAssignment] = useState("any"); // any | assigned | unassigned | users
  const [assignees, setAssignees] = useState([]);
  const [createdPreset, setCreatedPreset] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [contacted, setContacted] = useState("any"); // any | no | yes | range
  const [contactedFrom, setContactedFrom] = useState("");
  const [contactedTo, setContactedTo] = useState("");
  const [productId, setProductId] = useState("");
  const [keyword, setKeyword] = useState("");
  const [selectedColumns, setSelectedColumns] = useState(() => loadSavedColumns(columns));
  const [fileType, setFileType] = useState("csv");
  const [count, setCount] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (isOpen) setScope(initialScope === "selected" && !hasSelection ? "all" : initialScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialScope]);

  // API params for the chosen scope (null for "selected", which uses ids)
  const params = useMemo(() => {
    const p = {};
    if (scope === "current") {
      const f = currentFilters;
      if (f.stage && f.stage !== "all") p.stage = f.stage;
      if (f.status) p.status = f.status;
      if (f.assignment) p.assignment = f.assignment;
      if (f.assignedTo) p.assignedTo = f.assignedTo;
      if (f.search?.trim()) p.search = f.search.trim();
    } else if (scope === "custom") {
      if (stages.length) p.stage = stages.join(",");
      if (statuses.length) p.status = statuses.join(",");
      if (priorities.length) p.priority = priorities.join(",");
      if (sources.length) p.source = sources.join(",");
      if (assignment === "assigned" || assignment === "unassigned") p.assignment = assignment;
      if (assignment === "users" && assignees.length) p.assignedTo = assignees.join(",");
      if (createdFrom) p.createdFrom = dayStart(createdFrom);
      if (createdTo) p.createdTo = dayEnd(createdTo);
      if (contacted === "no" || contacted === "yes") p.contacted = contacted;
      if (contacted === "range") {
        if (contactedFrom) p.contactedFrom = dayStart(contactedFrom);
        if (contactedTo) p.contactedTo = dayEnd(contactedTo);
      }
      if (productId) p.productId = productId;
      if (keyword.trim()) p.search = keyword.trim();
    } else if (scope === "selected") {
      return null;
    }
    if (currentFilters.sortBy) p.sortBy = currentFilters.sortBy;
    if (currentFilters.sortOrder) p.sortOrder = currentFilters.sortOrder;
    return p;
  }, [scope, currentFilters, stages, statuses, priorities, sources, assignment, assignees,
    createdFrom, createdTo, contacted, contactedFrom, contactedTo, productId, keyword]);

  // Live "N leads will be exported" count
  useEffect(() => {
    if (!isOpen) return undefined;
    if (scope === "selected") {
      setCount(selectedCount);
      return undefined;
    }
    setCount(null);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await leadsAPI.getAll({ ...params, limit: 1, page: 1 });
        if (!cancelled) setCount(res.data?.pagination?.total ?? 0);
      } catch {
        if (!cancelled) setCount(null);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [isOpen, scope, params, selectedCount]);

  const fetchAll = async (p, toastId) => {
    const all = [];
    for (let page = 1; ; page++) {
      const res = await leadsAPI.getAll({ ...p, limit: 1000, page });
      const batch = res.data?.data || [];
      all.push(...batch);
      const totalPages = res.data?.pagination?.totalPages || 1;
      if (batch.length === 0 || page >= totalPages) break;
      toast.loading(`Preparing export... ${all.length} leads`, { id: toastId });
    }
    return all;
  };

  const handleExport = async () => {
    if (selectedColumns.length === 0) {
      toast.error("Pick at least one column");
      return;
    }
    setExporting(true);
    const toastId = toast.loading("Preparing export...");
    try {
      let rows;
      if (scope === "selected") {
        const sortParams = {};
        if (currentFilters.sortBy) sortParams.sortBy = currentFilters.sortBy;
        if (currentFilters.sortOrder) sortParams.sortOrder = currentFilters.sortOrder;
        if (selection.mode === "count") {
          // "Select by count" = the first N leads of the current table view
          const f = currentFilters;
          const p = { ...sortParams };
          if (f.stage && f.stage !== "all") p.stage = f.stage;
          if (f.status) p.status = f.status;
          if (f.assignment) p.assignment = f.assignment;
          if (f.assignedTo) p.assignedTo = f.assignedTo;
          if (f.search?.trim()) p.search = f.search.trim();
          rows = (await fetchAll(p, toastId)).slice(0, selection.count);
        } else {
          const ids = new Set(selection.ids);
          const onPage = selection.pageLeads.filter((l) => ids.has(l._id));
          rows =
            onPage.length === ids.size
              ? onPage
              : (await fetchAll(sortParams, toastId)).filter((l) => ids.has(l._id));
        }
      } else {
        rows = await fetchAll(params, toastId);
      }
      toast.dismiss(toastId);

      if (rows.length === 0) {
        toast.error("No leads match these options");
        return;
      }

      const cols = columns.filter((c) => selectedColumns.includes(c.key));
      const header = cols.map((c) => c.label);
      const body = rows.map((l) => cols.map((c) => String(c.get(l) ?? "")));
      const fileBase = `leads-export-${scope}-${format(new Date(), "yyyy-MM-dd")}`;

      if (fileType === "xlsx") {
        const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
        sheet["!cols"] = cols.map((c) => ({ wch: Math.max(12, c.label.length + 2) }));
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, "Leads");
        XLSX.writeFile(book, `${fileBase}.xlsx`);
      } else {
        const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const csv = [header, ...body].map((r) => r.map(cell).join(",")).join("\n");
        // BOM so Excel opens names in other scripts (Hindi, Arabic) correctly
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileBase}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      }

      try {
        localStorage.setItem(COLUMNS_KEY, JSON.stringify(selectedColumns));
      } catch {
        // column choice just isn't remembered
      }
      toast.success(`Exported ${rows.length} leads`);
      onClose();
    } catch (error) {
      console.error("Export error:", error);
      toast.dismiss(toastId);
      toast.error("Failed to export leads");
    } finally {
      setExporting(false);
    }
  };

  const resetCustom = () => {
    setStages([]); setStatuses([]); setPriorities([]); setSources([]);
    setAssignment("any"); setAssignees([]);
    setCreatedPreset(""); setCreatedFrom(""); setCreatedTo("");
    setContacted("any"); setContactedFrom(""); setContactedTo("");
    setProductId(""); setKeyword("");
  };

  const currentSummary = [
    currentFilters.stage && currentFilters.stage !== "all" && `Stage: ${stageLabel(currentFilters.stage)}`,
    currentFilters.status && `Status: ${formatLabel(currentFilters.status)}`,
    currentFilters.assignment && formatLabel(currentFilters.assignment),
    currentFilters.assignedTo && `Assigned: ${personName((users || []).find((u) => u._id === currentFilters.assignedTo)) || "selected user"}`,
    currentFilters.search?.trim() && `Search: "${currentFilters.search.trim()}"`,
  ].filter(Boolean);

  const scopes = [
    { key: "all", icon: HiOutlineCollection, label: "All leads", desc: "Every lead in your organization" },
    {
      key: "current",
      icon: HiOutlineEye,
      label: "Current view",
      desc: currentSummary.length ? currentSummary.join(" · ") : "No filters applied — same as all leads",
    },
    {
      key: "selected",
      icon: HiOutlineCheckCircle,
      label: "Selected leads",
      badge: selectedCount,
      desc: hasSelection ? "Only the leads you ticked in the table" : "Tick leads in the table first",
      disabled: !hasSelection,
    },
    { key: "custom", icon: HiOutlineAdjustments, label: "Custom filters", desc: "Stage, status, priority, source, assignee, dates…" },
  ];

  const activeFilterCount =
    stages.length + statuses.length + priorities.length + sources.length +
    (assignment !== "any" ? 1 : 0) + (createdFrom || createdTo ? 1 : 0) +
    (contacted !== "any" ? 1 : 0) + (productId ? 1 : 0) + (keyword.trim() ? 1 : 0);

  const fieldInput =
    "w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-gray-800 dark:text-slate-100 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20";
  const card = "rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/40 p-3.5 sm:p-4";

  const setGroup = (groupKey, on) => {
    const keys = columns.filter((c) => c.group === groupKey).map((c) => c.key);
    setSelectedColumns((prev) => {
      const next = on ? [...new Set([...prev, ...keys])] : prev.filter((k) => !keys.includes(k));
      return columns.map((c) => c.key).filter((k) => next.includes(k));
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Leads" size="xl">
      <div className="space-y-6">
        {/* 1. Which leads */}
        <section>
          <Step n={1} title="Which leads do you want to export?" />
          <div role="radiogroup" className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {scopes.map((sc) => (
              <ChoiceCard
                key={sc.key}
                selected={scope === sc.key}
                disabled={sc.disabled}
                onSelect={() => setScope(sc.key)}
                icon={sc.icon}
                title={sc.label}
                description={sc.desc}
                badge={sc.badge}
              />
            ))}
          </div>
        </section>

        {/* 2. Custom filters */}
        {scope === "custom" && (
          <section>
            <Step
              n={2}
              title="Filter leads"
              right={
                activeFilterCount > 0 && (
                  <button type="button" onClick={resetCustom} className="text-xs sm:text-sm font-medium text-primary-600 hover:text-primary-700">
                    Clear all ({activeFilterCount})
                  </button>
                )
              }
            />
            <div className="space-y-3">
              <div className={card}>
                <Section title="Pipeline stage" count={stages.length} onClear={() => setStages([])}>
                  <div className="flex flex-wrap gap-1.5">
                    {(stageOptions || []).map((st) => (
                      <Chip key={st.value} active={stages.includes(st.value)} onClick={() => setStages(toggleIn(stages, st.value))}>
                        {st.label}
                      </Chip>
                    ))}
                  </div>
                </Section>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className={card}>
                  <Section title="Status" count={statuses.length} onClear={() => setStatuses([])}>
                    <div className="flex flex-wrap gap-1.5">
                      {STATUS_VALUES.map((v) => (
                        <Chip key={v} active={statuses.includes(v)} onClick={() => setStatuses(toggleIn(statuses, v))}>
                          {formatLabel(v)}
                        </Chip>
                      ))}
                    </div>
                  </Section>
                </div>
                <div className={`${card} space-y-4`}>
                  <Section title="Priority" count={priorities.length} onClear={() => setPriorities([])}>
                    <div className="flex flex-wrap gap-1.5">
                      {PRIORITY_VALUES.map((v) => (
                        <Chip key={v} active={priorities.includes(v)} onClick={() => setPriorities(toggleIn(priorities, v))}>
                          {formatLabel(v)}
                        </Chip>
                      ))}
                    </div>
                  </Section>
                  <Section title="Assigned to" count={assignment !== "any" ? 1 : 0} onClear={() => { setAssignment("any"); setAssignees([]); }}>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        ["any", "Anyone"],
                        ["assigned", "Assigned"],
                        ["unassigned", "Unassigned"],
                        ["users", "Specific people"],
                      ].map(([k, label]) => (
                        <Chip key={k} active={assignment === k} onClick={() => setAssignment(k)}>
                          {label}
                        </Chip>
                      ))}
                    </div>
                    {assignment === "users" && (
                      <div className="mt-2.5 p-2.5 rounded-lg bg-gray-50 dark:bg-slate-900/60 border border-gray-100 dark:border-slate-700">
                        <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                          {(users || []).map((u) => (
                            <Chip key={u._id} active={assignees.includes(u._id)} onClick={() => setAssignees(toggleIn(assignees, u._id))}>
                              {personName(u) || u.email}
                            </Chip>
                          ))}
                          {(users || []).length === 0 && <span className="text-xs text-gray-500">No telecallers found</span>}
                        </div>
                      </div>
                    )}
                  </Section>
                </div>
              </div>

              <div className={card}>
                <Section title="Source" count={sources.length} onClear={() => setSources([])}>
                  <div className="flex flex-wrap gap-1.5">
                    {[...new Set([...SOURCE_VALUES, ...sources])].map((v) => (
                      <Chip key={v} active={sources.includes(v)} onClick={() => setSources(toggleIn(sources, v))}>
                        {SOURCE_LABELS[v] || v}
                      </Chip>
                    ))}
                    <input
                      type="text"
                      placeholder="+ Add other source"
                      title="Type a source name and press Enter"
                      className="px-3 py-1.5 rounded-full border border-dashed border-gray-300 dark:border-slate-600 bg-transparent text-xs sm:text-sm w-44 focus:outline-none focus:border-primary-500"
                      onKeyDown={(e) => {
                        const v = e.currentTarget.value.trim();
                        if (e.key === "Enter" && v) {
                          e.preventDefault();
                          if (!sources.includes(v)) setSources([...sources, v]);
                          e.currentTarget.value = "";
                        }
                      }}
                    />
                  </div>
                </Section>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className={card}>
                  <Section
                    title="Created date"
                    count={createdFrom || createdTo ? 1 : 0}
                    onClear={() => { setCreatedPreset(""); setCreatedFrom(""); setCreatedTo(""); }}
                  >
                    <div className="flex flex-wrap gap-1.5 mb-2.5">
                      {DATE_PRESETS.map((p) => (
                        <Chip
                          key={p.key}
                          active={createdPreset === p.key}
                          onClick={() => {
                            const [from, to] = p.range();
                            setCreatedPreset(p.key);
                            setCreatedFrom(from);
                            setCreatedTo(to);
                          }}
                        >
                          {p.label}
                        </Chip>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <span className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">From</span>
                        <input type="date" className={fieldInput} value={createdFrom} max={createdTo || undefined}
                          onChange={(e) => { setCreatedFrom(e.target.value); setCreatedPreset(""); }} />
                      </label>
                      <label className="block">
                        <span className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">To</span>
                        <input type="date" className={fieldInput} value={createdTo} min={createdFrom || undefined}
                          onChange={(e) => { setCreatedTo(e.target.value); setCreatedPreset(""); }} />
                      </label>
                    </div>
                  </Section>
                </div>
                <div className={card}>
                  <Section
                    title="Last contacted"
                    count={contacted !== "any" ? 1 : 0}
                    onClear={() => { setContacted("any"); setContactedFrom(""); setContactedTo(""); }}
                  >
                    <div className="flex flex-wrap gap-1.5 mb-2.5">
                      {[
                        ["any", "Any time"],
                        ["no", "Never contacted"],
                        ["yes", "Contacted"],
                        ["range", "Between dates"],
                      ].map(([k, label]) => (
                        <Chip key={k} active={contacted === k} onClick={() => setContacted(k)}>
                          {label}
                        </Chip>
                      ))}
                    </div>
                    {contacted === "range" && (
                      <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                          <span className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">From</span>
                          <input type="date" className={fieldInput} value={contactedFrom} max={contactedTo || undefined}
                            onChange={(e) => setContactedFrom(e.target.value)} />
                        </label>
                        <label className="block">
                          <span className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">To</span>
                          <input type="date" className={fieldInput} value={contactedTo} min={contactedFrom || undefined}
                            onChange={(e) => setContactedTo(e.target.value)} />
                        </label>
                      </div>
                    )}
                  </Section>
                </div>
              </div>

              <div className={`${card} grid grid-cols-1 sm:grid-cols-2 gap-3`}>
                <Section title="Product" count={productId ? 1 : 0} onClear={() => setProductId("")}>
                  <select className={fieldInput} value={productId} onChange={(e) => setProductId(e.target.value)}>
                    <option value="">Any product</option>
                    {(products || []).map((p) => (
                      <option key={p._id} value={p._id}>{p.name}</option>
                    ))}
                  </select>
                </Section>
                <Section title="Keyword" count={keyword.trim() ? 1 : 0} onClear={() => setKeyword("")}>
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Name, phone or email contains…"
                    className={fieldInput}
                  />
                </Section>
              </div>
            </div>
          </section>
        )}

        {/* 3. Columns */}
        <section>
          <Step
            n={scope === "custom" ? 3 : 2}
            title="Columns to include"
            right={
              <div className="flex items-center gap-1 text-xs">
                {[
                  ["All", () => setSelectedColumns(columns.map((c) => c.key))],
                  ["Default", () => setSelectedColumns(columns.filter((c) => c.on).map((c) => c.key))],
                  ["None", () => setSelectedColumns([])],
                ].map(([label, fn]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={fn}
                    className="px-2 py-1 rounded-md font-medium text-gray-600 hover:bg-gray-100 hover:text-primary-700 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    {label}
                  </button>
                ))}
              </div>
            }
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            {COLUMN_GROUPS.map((g) => {
              const groupCols = columns.filter((c) => c.group === g.key);
              const onCount = groupCols.filter((c) => selectedColumns.includes(c.key)).length;
              const allOn = onCount === groupCols.length;
              return (
                <div key={g.key} className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                  <label className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 shrink-0"
                      checked={allOn}
                      ref={(el) => { if (el) el.indeterminate = onCount > 0 && !allOn; }}
                      onChange={() => setGroup(g.key, !allOn)}
                    />
                    <span
                      className="text-sm font-semibold text-gray-700 dark:text-slate-200 truncate"
                      title={`${onCount} of ${groupCols.length} selected`}
                    >
                      {g.label}
                    </span>
                  </label>
                  <div className="p-2 space-y-0.5">
                    {groupCols.map((c) => (
                      <label
                        key={c.key}
                        className="flex items-center gap-2 px-1.5 py-1 rounded-md text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 shrink-0"
                          checked={selectedColumns.includes(c.key)}
                          onChange={() =>
                            setSelectedColumns((prev) =>
                              prev.includes(c.key)
                                ? prev.filter((k) => k !== c.key)
                                : columns.map((x) => x.key).filter((k) => k === c.key || prev.includes(k)),
                            )
                          }
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* 4. Format */}
        <section>
          <Step n={scope === "custom" ? 4 : 3} title="File format" />
          <div role="radiogroup" className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:max-w-xl">
            <ChoiceCard
              selected={fileType === "csv"}
              onSelect={() => setFileType("csv")}
              icon={HiOutlineDocumentText}
              title="CSV (.csv)"
              description="Works everywhere and can be imported back"
            />
            <ChoiceCard
              selected={fileType === "xlsx"}
              onSelect={() => setFileType("xlsx")}
              icon={HiOutlineTable}
              title="Excel (.xlsx)"
              description="Opens directly in Microsoft Excel"
            />
          </div>
        </section>
      </div>

      {/* Footer stays visible while the popup scrolls: sticky inside the
          desktop panel; fixed to the screen on phones, where the full-screen
          panel is not the scrolling element */}
      <div className="h-36 sm:hidden" aria-hidden="true" />
      <div className="fixed sm:sticky inset-x-0 bottom-0 z-20 sm:-mx-5 sm:-mb-5 sm:mt-6 px-5 py-3.5 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] flex flex-col-reverse sm:flex-row sm:items-center gap-3">
        <div className="text-sm text-gray-600 dark:text-slate-300 sm:mr-auto">
          {count === null ? (
            <span className="text-gray-400">Counting leads…</span>
          ) : (
            <>
              <strong className="text-gray-900 dark:text-slate-100 text-base">{count.toLocaleString()}</strong>{" "}
              lead{count === 1 ? "" : "s"}
              <span className="text-gray-400 mx-1.5">·</span>
              {selectedColumns.length} column{selectedColumns.length === 1 ? "" : "s"}
              <span className="text-gray-400 mx-1.5">·</span>
              {fileType === "xlsx" ? "Excel" : "CSV"}
            </>
          )}
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} disabled={exporting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<HiOutlineDownload className="w-4 h-4" />}
            onClick={handleExport}
            loading={exporting}
            disabled={exporting || count === 0 || selectedColumns.length === 0}
          >
            {count ? `Export ${count.toLocaleString()} leads` : "Export"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ExportLeadsModal;
